import React, { useState } from 'react';
import {
  Fade,
  Typography,
  Button,
  Banner,
} from '@forgeglobal/design-framework';
import { object, string, ref } from 'yup';

import {
  Form,
  Field,
  FormProps,
  FormRenderProps,
  FieldRenderProps,
} from '@tcs/common/components/atoms/Form';
import TextField from '@tcs/common/components/atoms/TextField';
import ValidationWidget from '@tcs/common/components/atoms/ValidationWidget';
import useSession from '@tcs/client-portal-common/hooks/useSession';

import { useStyles } from './style';
import { passwordRules } from './PasswordRules';

interface ChangePasswordFormProps {
  showPasswordFail: boolean;
}

const ChangePasswordForm = ({
  onSubmit,
  onCancel,
  initialValues,
  showPasswordFail,
}: FormProps & ChangePasswordFormProps) => {
  const classes = useStyles();

  const {
    claims: { clientPortalIdentity },
  } = useSession();
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordTouched, setNewPasswordTouched] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const onFormValidate = (isValid): void => setIsFormValid(isValid);

  const updateValidationWidget = (e) => {
    setNewPassword((e.target as HTMLInputElement).value);
    setNewPasswordTouched(true);
  };

  let newPasswordSchema = string();

  passwordRules
    .slice()
    .reverse()
    .forEach((rule) => {
      newPasswordSchema = newPasswordSchema.test(
        rule.name,
        rule.message,
        rule.test
      );
    });

  newPasswordSchema = newPasswordSchema.required('Required field');

  const validationSchema = object({
    currentPassword: string().required('Required field'),
    newPassword: newPasswordSchema,
    confirmNewPassword: string()
      .oneOf(
        [ref('newPassword'), null],
        'Passwords don’t match. Please try again'
      )
      .required('Required field'),
  });

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      onValidate={onFormValidate}
      validationSchema={validationSchema}
      render={({ handleSubmit, submitting }: FormRenderProps) => {
        return (
          validationSchema && (
            <form
              data-test-id="change-password-form"
              onSubmit={handleSubmit}
              className={classes.form}>
              <Fade
                in={showPasswordFail}
                timeout={750}
                style={{ display: showPasswordFail ? undefined : 'none' }}>
                <div className={classes.banner}>
                  <Banner severity="error" bannerType="inline">
                    <Typography
                      variant="body2"
                      color="textPrimary"
                      data-test-id="login-message">
                      Password change was unsuccessful.
                    </Typography>
                  </Banner>
                </div>
              </Fade>
              <div className={classes.container}>
                <div className={classes.title}>
                  <Typography variant="h5" data-test-id="sub-title">
                    Change Password
                  </Typography>
                </div>

                <div className={classes.fields}>
                  <div className={classes.field}>
                    <Field
                      name="currentPassword"
                      render={(props: FieldRenderProps<string>) => (
                        <TextField
                          data-private
                          data-test-id="current-password"
                          type="password"
                          label="Current password"
                          autoComplete="current-password"
                          className={classes.textField}
                          {...props}
                        />
                      )}
                    />
                  </div>
                  <div
                    className={classes.field}
                    data-test-id="validation-widget">
                    <ValidationWidget
                      value={newPassword}
                      title="New password must:"
                      rules={passwordRules}
                      touched={newPasswordTouched}
                    />
                  </div>
                  {/* hidden field for improved autocomplete experience */}
                  <div hidden>
                    <input
                      value={clientPortalIdentity}
                      autoComplete="username"
                      disabled
                    />
                  </div>
                  <div className={classes.field}>
                    <Field
                      name="newPassword"
                      render={({ meta, input }: FieldRenderProps<string>) => (
                        <TextField
                          data-private
                          data-test-id="new-password"
                          type="password"
                          autoComplete="new-password"
                          className={classes.textField}
                          onBlur={(e) => {
                            input.onBlur(e);
                            updateValidationWidget(e);
                          }}
                          onChange={(e) => {
                            input.onChange(e);
                            updateValidationWidget(e);
                          }}
                          label="New password"
                          input={input}
                          meta={{
                            ...meta,
                            /* override form error to prevent duplication */
                            error: undefined,
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className={classes.field}>
                    <Field
                      name="confirmNewPassword"
                      render={(props: FieldRenderProps<string>) => (
                        <TextField
                          data-private
                          data-test-id="confirm-new-password"
                          type="password"
                          label="Confirm new password"
                          autoComplete="new-password"
                          className={classes.textField}
                          {...props}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className={classes.buttonsContainer}>
                  <Button
                    data-test-id="cancel-change-password"
                    size="medium"
                    color="secondary"
                    onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    data-test-id="submit-change-password"
                    className={classes.primaryButton}
                    size="medium"
                    color="primary"
                    type="submit"
                    disabled={submitting || !isFormValid}>
                    Save
                  </Button>
                </div>
              </div>
            </form>
          )
        );
      }}
    />
  );
};

export default ChangePasswordForm;
